import type { Messages } from './en';

export const zhCN: Messages = {
  status: {
    queued: '排队中',
    analyzing: '分析中',
    success: '成功',
    failed: '失败',
  },
  table: {
    columns: '列',
    toggle_columns: '显示列',
    resize_column: '调整列宽',
    resize_column_hint: '拖动调整宽度，双击恢复默认',
    select_row: '选择 {{name}}',
    select_all_rows: '选择当前页全部行',
    select_all_rows_limited: '最多选择 {{max}} 个当前页行',
  },
  common: {
    loading: '加载中…',
    error: '出错了',
    retry: '重试',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    confirm: '确认',
    back: '返回',
    forward: '前进',
    sign_out: '退出登录',
    no_data: '暂无数据',
    no_results: '无匹配结果',
    n_selected: '已选 {{count}} 项',
    clear_filters: '清除筛选',
    expand: '展开',
    collapse: '收起',
    view_details: '查看详情',
    row_actions: '{{name}} 的操作',
    resize_panel: '调整面板宽度',
    resize_panel_hint: '拖动调整宽度，双击恢复默认',
    page_prev: '上一页',
    page_next: '下一页',
    page_prev_aria: '上一页',
    page_next_aria: '下一页',
    copyright: '© {{year}} 联想（Lenovo）· 仅供内部使用',
  },
  docs: {
    eyebrow: '平台文档',
    title: '转储诊断',
    subtitle:
      '从一台机器蓝屏，到一份可以归档的结论：配置测试机、采集与上传转储、读懂 AI 分析、把报告交付出去。',
    toc: '本页目录',
    updated: '依据 SOP v1.1 · 2026-09-26 更新',
    cta_download: '获取客户端',
    cta_quickstart: '快速开始',
    cta_title: '准备就绪？',
    cta_body: '下载 BSOD Scout，几分钟内完成第一台测试机的接入。',
    cta_button: '前往下载',
    // Labels shared with the release usage blocks on /download.
    code_label_config: 'bsod-relay.json',
    code_label_console: '控制台',
    groups: [
      {
        title: '认识平台',
        sections: [
          {
            id: 'overview',
            no: '01',
            title: '平台做什么',
            blocks: [
              {
                p: '蓝屏转储诊断平台（Crash Dump Diagnostics）把 Windows 蓝屏从现场取证到根因结论的整条链路自动化：机器蓝屏后，转储与诊断日志被自动采集上传，平台排队执行 AI 辅助分析，工程师复核结论、把重复问题归并成案例，再把报告交付给需要的人。',
              },
              {
                table: {
                  head: ['组件', '形态', '职责'],
                  rows: [
                    [
                      'BSOD Scout',
                      'Windows 单文件 EXE（双击 → UAC → 图形界面）',
                      '配置崩溃转储、把日志与转储一起采集、上传到指定项目，并可注册开机自动采集任务。它不做分析，只负责把证据完整地送上平台。',
                    ],
                    [
                      'bsod-relay',
                      'Go 单文件进程（约 3.8 MB，无运行时依赖）',
                      '跑在同时能连实验室网络与公网的主机上的 HTTP `CONNECT` 正向代理，外加 UDP 发现应答。只搬字节：不终结 TLS、不落盘、不校验凭据。',
                    ],
                    [
                      'Web 控制台',
                      '浏览器 SPA',
                      '提交分析、跟进事件与 AI 结论、归并案例、管理设备/成员/项目、签发上传 PIN、对外分享只读报告。平台超管另有独立 Admin 控制台。',
                    ],
                  ],
                },
              },
              {
                p: '一次上传的完整数据流：Scout 登记事件并向后端申请预签名地址 → 转储与附件直传 OSS（大文件分片）→ 后端落库并投递分析任务 → Worker 用 CDB / WinDbg 做取证循环 → 结论（markdown 加结构化字段）回写后端 → 工程师在浏览器里查看、导出、分享。',
              },
            ],
          },
          {
            id: 'quickstart',
            no: '02',
            title: '快速开始',
            blocks: [
              {
                p: '按角色挑一条路径即可，它们互不冲突：同一个人可以先用第三条把测试机接上，再回到第一条。',
              },
              {
                table: {
                  head: ['你是谁', '从这里开始'],
                  rows: [
                    [
                      '工程师，手上有一个 dump',
                      '打开 `/submit`，选好项目，附上 `.zip`，平台立即排队分析。',
                    ],
                    ['测试机，希望自动采集', '登录一次并安装开机任务；之后每次蓝屏都会自动上传。'],
                    ['实验室 IT，测试网段不能出网', '在可出网的桥接主机上部署 `bsod-relay`。'],
                  ],
                },
              },
              { code: { label: '控制台', text: 'https://bsod.lenovo.com/submit' } },
              {
                code: {
                  label: 'PowerShell',
                  text: 'BsodScout.exe -Login\nBsodScout.exe -InstallTask -ProjectId <项目ID>',
                },
              },
              {
                code: {
                  label: '桥接主机',
                  text: 'go build -o bsod-relay ./cmd/bsod-relay\n./bsod-relay -check -config bsod-relay.json\n./bsod-relay -check-egress -config bsod-relay.json',
                },
              },
              {
                warn: '「上传成功」以控制台里事件状态 = Success 为准，不是「本地已生成 zip」。事件长时间停在 Queued / Analyzing 时，先看 Worker 与队列是否健康，再决定是否重传。',
              },
              {
                img: {
                  src: '/sop/img/projects.png',
                  alt: '项目列表',
                  caption:
                    '**图 3-3**　项目列表：点任意一行进入该项目控制台；控制台页面顶部的项目切换器可随时跳转。',
                },
              },
              {
                img: {
                  src: '/sop/img/project-overview.png',
                  alt: '项目概览',
                  caption:
                    '**图 3-4**　项目概览：指标磁贴可点击跳到已筛选的事件列表；左侧为该项目的固定导航。',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Web 控制台',
        sections: [
          {
            id: 'access',
            no: '03',
            title: '登录、账号与权限',
            blocks: [
              {
                p: '平台入口是 `https://bsod.lenovo.com`，支持公司 SSO（ITCode / Keycloak / ADFS）或平台本地账号（邮箱 + 密码）。首次登录需先阅读并确认合规提示。界面打开时默认英文，右上角地球图标可切换中文，选择按浏览器记住。',
              },
              {
                img: {
                  src: '/sop/img/login-compliance.png',
                  alt: '首次登录的人工智能政策使用提示',
                  caption:
                    '**图 3-1**　首次访问：转储诊断服务平台（Crash Dump Diagnostics）的 AI 政策与隐私声明使用提示，确认后不再弹出。',
                },
              },
              {
                img: {
                  src: '/sop/img/login.png',
                  alt: '登录页：SSO 与邮箱登录',
                  caption: '**图 3-2**　登录页：SSO 或本地账号；页脚提供 Docs / Download 入口。',
                },
              },
              {
                table: {
                  head: ['角色', '能做什么'],
                  rows: [
                    ['PA', '只看 + 上传 + 评论 + 编辑事件描述。'],
                    ['Engineer', '在 PA 之上可编辑案例、外链与评论。'],
                    ['SA Leader', '在 Engineer 之上可管理事件（重跑分析、删除）、成员与项目设置。'],
                    [
                      '公司管理员 / 平台超管',
                      '每请求算出的等效角色，不写进成员表；超管另见独立 Admin 控制台。',
                    ],
                  ],
                },
              },
              {
                p: 'Account 页维护个人资料与密码，并签发 12 位上传 PIN（形如 `A7K2-9QXM-4T3B`），供没有工程师账号的测试机上传。PIN 不绑定项目或机器，有效期内可重复使用；大小写、连字符与易混字符（O/0、I/L/1）均可互换。',
              },
              {
                img: {
                  src: '/sop/img/account-pin.png',
                  alt: '账号页：生成上传 PIN',
                  caption:
                    '**图 3-13**　Generate uploader PIN：设定有效天数 → 生成 → 抄给测试机使用；可随时 Revoke。',
                },
              },
              {
                warn: 'PIN 连续输错会触发限流：先按来源地址退避，达到阈值后整站暂停兑换。不要在脚本里重试猜测。',
              },
            ],
          },
          {
            id: 'submit',
            no: '04',
            title: '手工提交一次分析',
            blocks: [
              {
                p: '没有测试机、或只想补交一个 dump 时，直接在浏览器上传：点事件列表右上角的 **New analysis**，在右侧抽屉里拖入 `.zip`、选择项目、按需填写描述，然后 Start upload。',
              },
              {
                ul: [
                  '提交前用 `#公开 ID` 复核项目：上传归属错了项目，等于没采，需要的人看不到。',
                  '只接受 `.zip`；浏览器在本地算 SHA-256 后经 TLS 上传，大文件自动分片续传。',
                  '上传成功会直接跳到新事件详情。',
                ],
              },
              {
                p: 'zip 里除 dump 外，建议一并放入 `systeminfo.txt`、`msinfo32.txt`、驱动列表与事件日志，分析结论的准确度会明显提高。Scout 会自动打包这些附件，手工上传时需要自己附上。',
              },
              {
                img: {
                  src: '/sop/img/submit.png',
                  alt: '手工提交分析页面',
                  caption:
                    '**图 3-5**　New analysis 抽屉：先放 dump、再选项目、可选填描述；文件在本地完成 SHA-256 校验后经 TLS 上传。',
                },
              },
            ],
          },
          {
            id: 'events',
            no: '05',
            title: '事件与 AI 结论',
            blocks: [
              {
                p: '每次上传对应一条事件。列表按项目范围与状态筛选，页带右上角的项目选择器可以把范围放宽到全部可见项目；搜索框支持字段语法，分析中的事件通过 SSE 自动刷新，不需要手动刷新页面。',
              },
              {
                img: {
                  src: '/sop/img/events.png',
                  alt: '事件列表',
                  caption:
                    '**图 3-6**　事件列表：事件号、错误码、标题、上传时间与状态；展开一行看描述与故障模块，右上 **Columns** 可选列。',
                },
              },
              {
                code: {
                  label: '搜索语法',
                  text: 'bugcheck:0x9F status:success\nwifi resume\n2606-1',
                },
              },
              {
                table: {
                  head: ['状态', '含义与处置'],
                  rows: [
                    [
                      'Queued',
                      '已上传，等待 Worker 领取。长时间不动，去 Admin → Queue Health 看队列。',
                    ],
                    ['Analyzing', 'Worker 正在做取证分析，页面自动刷新。'],
                    ['Success', '已有 AI 结论，可以直接阅读报告。'],
                    [
                      'Failed',
                      '分析中断（dump 不完整、方案超时等）。打开事件点 Reanalyze 重跑，或看失败原因。',
                    ],
                  ],
                },
              },
              {
                table: {
                  head: ['页签', '内容'],
                  rows: [
                    ['Summary', '一句话结论、可疑模块、栈顶。'],
                    ['Report', '完整报告——根因、证据、建议动作。这是对外沟通的主要材料。'],
                    [
                      'Stack / Modules / System',
                      '调用栈、加载模块，以及机器快照（OS、BIOS、CPU、内存、网卡）。',
                    ],
                    ['Files / Runs', '原始 dump 与附件下载，以及每一次分析轮次，可对比重跑结果。'],
                  ],
                },
              },
              {
                p: '常用动作：**Files** 下载原始 dump，**Reanalyze**（SA Leader 以上）重跑分析，**Share** 生成只读链接，右上 **Download** 导出报告，**Files / Attach** 追加后来补的日志。',
              },
              {
                img: {
                  src: '/sop/img/event-detail.png',
                  alt: '事件详情：AI 结论与栈顶',
                  caption:
                    '**图 3-7**　事件详情（Summary 页签）：要点行给出 BUGCHECK / MODULE / COMPUTER，AI verdict 卡左侧是一句话结论、右侧是栈顶帧（crash site）。',
                },
              },
              {
                img: {
                  src: '/sop/img/event-report.png',
                  alt: '事件详情：Report 页签',
                  caption:
                    '**图 3-8**　Report 页签：完整 markdown 结论（根因、证据、建议动作），可直接复制进缺陷单。',
                },
              },
            ],
          },
          {
            id: 'cases',
            no: '06',
            title: '案例、设备与成员',
            blocks: [
              {
                p: '同一个根因往往在多台机器、多个批次重复出现。把相关事件归并成一个 Case，跟踪的就是「一个问题」而不是「一堆 dump」：案例有标题、状态（open/closed）、事件计数与描述。',
              },
              {
                img: {
                  src: '/sop/img/cases.png',
                  alt: '案例列表',
                  caption: '**图 3-9**　案例列表：可见每个案例挂了多少条事件、最后更新时间。',
                },
              },
              {
                p: '上传过 dump 的机器会按 SMBIOS UUID / 序列号 / MAC 自动归并为设备，因此同一台机器反复蓝屏只算一台。可用主机名、MAC、序列号、机型搜索，判断「是单台机器还是同一批次」。SA Leader 及以上可在 Members 中增减成员、调整角色。',
              },
              {
                img: {
                  src: '/sop/img/devices.png',
                  alt: '设备列表',
                  caption: '**图 3-10**　设备列表：主机名、MAC、序列号、机型、SKU、最后上报时间。',
                },
              },
              {
                img: {
                  src: '/sop/img/members.png',
                  alt: '项目成员',
                  caption:
                    '**图 3-11**　成员管理：PA 只看+上传+评论+编辑事件描述，Engineer 可编辑案例，SA Leader 可管事件、成员与项目设置。',
                },
              },
              {
                ul: [
                  '同一 bugcheck + 同一可疑模块 → 建一个案例。',
                  '在案例描述里写「已复现条件 + 已确认结论 + 待修版本」。',
                  '把案例编号（如 `#2606-1`）写进缺陷单，便于回溯与验收。',
                ],
              },
            ],
          },
          {
            id: 'share',
            no: '07',
            title: '分享只读报告',
            blocks: [
              {
                p: '需要把结论发给非平台用户（供应商、兄弟团队）时，在事件详情点 Share 生成只读链接。链接带有效期、可随时吊销；打开者无需登录，只看得到该事件的标题、结论摘要与文件下载按钮。',
              },
              {
                img: {
                  src: '/sop/img/share-link.png',
                  alt: '只读分享页',
                  caption: '**图 3-12**　分享页：只读、可切换中英文、底部标注过期时间。',
                },
              },
              {
                warn: '分享链接等于免登录的 dump 下载权限。只发给需要的人、设置较短有效期（如 72 小时），任务结束后在 Share 面板吊销。',
              },
            ],
          },
          {
            id: 'admin',
            no: '08',
            title: '管理控制台',
            blocks: [
              {
                p: '平台超管（super admin）可见独立 Admin 控制台：用户、公司、Worker、PI 凭据、审计日志、队列健康与 OIDC 提供商。系统管理员还在「邮件通知」页配置分析完成后发送通知的邮箱。',
              },
              {
                table: {
                  head: ['入口', '用途'],
                  rows: [
                    ['Users / Companies', '账号、角色（Company admin / User）、启用与停用。'],
                    ['Queue Health', '分析队列是否堆积——事件一直 Queued 时先看这里。'],
                    ['Audit Logs', '谁在什么时候上传、删除、分享。'],
                    ['Workers / Relays', '分析 Worker 与内网中继的注册与心跳。'],
                    ['邮件通知', '分析成功时通知项目 SA Leader 的 Exchange 账号，可测试发送。'],
                  ],
                },
              },
              {
                p: '通知邮件只发给该项目的 SA Leader——工程师、PA 与公司管理员都不在收件人里。邮件里写明事件、bugcheck 与可疑模块，并直接链到报告。',
              },
              {
                img: {
                  src: '/sop/img/admin-users.png',
                  alt: 'Admin 控制台用户列表',
                  caption:
                    '**图 3-14**　Admin → Users：角色（Company admin / User）、状态、创建时间与启停操作。',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Scout 客户端',
        sections: [
          {
            id: 'scout-install',
            no: '09',
            title: '获取、安装与登录',
            blocks: [
              {
                p: '在 `/download` 下载单文件 EXE（`bsod-scout-<版本>-windows-x64.exe`，页面上有 SHA-256 可核对），放到任意目录即可。没有别的东西要装：不需要 .NET，也不需要 PowerShell 模块。',
              },
              {
                ul: [
                  '首次运行：双击 → UAC 提权 → 主界面。EXE 未做代码签名，SmartScreen 可能提示「Windows 已保护你的电脑」，选 **更多信息 → 仍要运行**。',
                  '已加域的机器启动时会提示「组策略可能覆盖本工具写入的注册表设置」，属正常提示。',
                  '采集与上传需要管理员权限：它要写 `HKLM\\...\\CrashControl` 并安装计划任务。',
                ],
              },
              {
                p: '转储类型建议全队统一设为**自动内存转储**或**完整内存转储**——分析质量取决于 dump 是否完整。域机器上组策略可能覆盖该设置，若策略强制小转储请申请例外。',
              },
            ],
          },
          {
            id: 'scout-pipeline',
            no: '10',
            title: '采集与上传：一条流水线',
            blocks: [
              { p: '**Run scan** 走的是同一条流水线，计划任务跑的也是它：' },
              {
                steps: [
                  '**连通性检测**——先发一次真实直连请求，不通才按阶梯找代理。',
                  '**收集日志 + 压缩转储**——msinfo32、systeminfo、driverquery、系统与应用程序事件日志、内置的 GatherLogs 包，加上最新一份转储，全部打进一个 zip。',
                  '**上传转储**——取预签名地址后分片 PUT，日志附件随事件一起提交。（原第 4 步「Upload logs」已并入本步。）',
                  '**备份到桌面**——zip 在本机留一份，便于离线复核或复传。',
                ],
              },
              {
                warn: '点 Start 后会锁定上传目标与账号控件，直到本次流水线结束：项目与描述是第 3 步才读取的，中途改动会让事件落到与界面显示不一致的项目。运行中关窗会先确认，选 **Keep running** 则原样保留。',
              },
            ],
          },
          {
            id: 'scout-auto',
            no: '11',
            title: '开机自动采集与免登录上传',
            blocks: [
              {
                p: '无人值守的测试机一次性配置好就不用再管：登录一次，然后注册绑定项目的开机任务。',
              },
              {
                code: {
                  label: 'PowerShell',
                  text: 'BsodScout.exe -Login\nBsodScout.exe -InstallTask -ProjectId <项目UUID>\nBsodScout.exe -RunScan -ProjectId <项目UUID> -Pin A7K2-9QXM-4T3B',
                },
              },
              {
                p: '任务名 `BsodScan`，开机 60 秒后以 SYSTEM 身份静默执行扫描上传；它的上传在 Events 里以 `uploader = Scout (scheduled)` 识别。下线机器时执行 `-UninstallTask`。',
              },
              {
                table: {
                  head: ['', '账号登录（JWT）', 'Uploader PIN'],
                  rows: [
                    ['适用场景', '工程师本人的机器', '共用测试机与产线上没有工程师账号的机器'],
                    ['权限', '按账号在项目内的角色', '只读项目查询 + 上传'],
                    [
                      '凭据存储',
                      '`auth.json`（SYSTEM + Administrators）',
                      '不落盘，仅在内存中兑换为短期上传令牌',
                    ],
                    [
                      '有效期 / 复用',
                      '随 access/refresh token 到期',
                      '按 PIN 自身有效期，可重复使用',
                    ],
                  ],
                },
              },
              {
                warn: '换机器名、搬目录或换版本后，任务仍指向旧的 EXE 路径。重新执行一次 `-InstallTask` 即可修复。',
              },
            ],
          },
          {
            id: 'scout-proxy',
            no: '12',
            title: '网络出口：代理与中继阶梯',
            blocks: [
              {
                p: '菜单 **☰ → Proxy** 决定「扫描、上传、登录」从哪条路出去：留空 = 自动探测，`direct` = 强制直连，填地址 = 固定使用该代理。',
              },
              {
                table: {
                  head: ['层级', '说明'],
                  rows: [
                    [
                      'L0 显式配置',
                      '菜单里的地址、`BSOD_PROXY` 或 `proxy.url`。命中就不再尝试别的。',
                    ],
                    [
                      'L2c 直连探测',
                      '对目标做一次 TCP + TLS 可达性检查，不发 HTTP 请求——能直连的机器不必先查系统代理。',
                    ],
                    ['L2 系统代理', 'WinINET / PAC，含机器级策略。'],
                    ['L2b 候选地址', '`proxy.discovery.candidates` 里预设的普通正向代理。'],
                    ['L3 局域网 relay', '先查缓存，再用 UDP 53535 广播发现。'],
                    ['L4 / L5', 'DNS 约定名，最后兜底为直连。'],
                  ],
                },
              },
              {
                p: '每个候选都要通过探针才算命中：先对代理发 `CONNECT`，再对真实主机做完整 TLS 握手并按系统证书链校验。中间解密 TLS 的代理会被拒绝，也不需要人工记指纹。',
              },
              {
                warn: '显式配置的代理不可用时会直接报 **Configured proxy unreachable**，不会偷偷改走直连。',
              },
            ],
          },
          {
            id: 'scout-logs',
            no: '13',
            title: '日志、状态文件与排错',
            blocks: [
              {
                table: {
                  head: ['路径', '用途'],
                  rows: [
                    ['`%ProgramData%\\bsod\\auth.json`', '登录令牌与后端地址（含凭据，勿外传）。'],
                    [
                      '`config.json`',
                      '出口代理与主题配置。EXE 同目录下的同名文件读取时优先，界面保存也写回那一份。',
                    ],
                    ['`project.json`', '记住的默认上传项目，BIOS 匹配不到时的回落值。'],
                    ['`proxy-cache.json`', '探测到的中继缓存，TTL 默认 30 分钟。'],
                    ['`logs\\BsodScout.log`', '运行日志——排错第一现场。'],
                    ['`桌面\\bsod-backup-<时间>.zip`', '本机留档，与上传内容一致。'],
                  ],
                },
              },
              {
                table: {
                  head: ['现象', '处理'],
                  rows: [
                    [
                      'SmartScreen 拦截 EXE',
                      'EXE 未做代码签名：**更多信息 → 仍要运行**，或走企业受信分发渠道。',
                    ],
                    [
                      'Step 1 长时间停在 Retrying in 3s…',
                      '直连不通且没找到可用代理：确认 relay 已启动、UDP 53535 与 TCP 3128 入站放行，或手工填一个地址。',
                    ],
                    [
                      'Configured proxy unreachable',
                      '手工配置的代理不可用且不会回退。修正地址，或改回自动 / 直连。',
                    ],
                    [
                      '上传到 100% 后 403',
                      'relay 出口白名单漏了 OSS 主机——把实际签名 URL 里的 host 加进 `allow.hosts`。',
                    ],
                    ['计划任务没跑', 'EXE 被移动或重命名：重新执行 `-InstallTask`。'],
                    [
                      '归属到了错误的项目',
                      'BIOS 前缀匹配到多个项目，或残留了上次的选择：核对前缀、重新选择，并用 `#ID` 复核。',
                    ],
                  ],
                },
              },
            ],
          },
        ],
      },
      {
        title: '内网中继',
        sections: [
          {
            id: 'relay',
            no: '14',
            title: 'bsod-relay：部署、白名单与自检',
            blocks: [
              {
                p: '测试网段没有出网、但有一台机器能连公网时，就在那台桥接主机上跑 `bsod-relay`。它是标准 HTTP `CONNECT` 正向代理：Scout 仍然连真实主机名，relay 只搬字节，TLS 端到端。没有证书要签、没有指纹要 pin、dump 也不会落在 relay 上。',
              },
              {
                code: {
                  label: '桥接主机',
                  text: 'go build -o bsod-relay ./cmd/bsod-relay\ncp config.example.json bsod-relay.json\n./bsod-relay -check -config bsod-relay.json\n./bsod-relay -check-egress -config bsod-relay.json\n./bsod-relay -config bsod-relay.json',
                },
              },
              {
                code: {
                  label: 'bsod-relay.json',
                  text: '{\n  "listen": "0.0.0.0:3128",\n  "beacon": { "enabled": true, "port": 53535 },\n  "allow": {\n    "hosts": ["bsod.lenovo.com", "platform-bsod.oss-cn-hangzhou.aliyuncs.com", "stscn.lenovo.com"],\n    "ports": [443]\n  },\n  "acl": { "allow_cidrs": ["10.0.0.0/8", "192.168.0.0/16"] },\n  "limits": { "max_connections": 64, "idle_timeout": "15m" }\n}',
                },
              },
              {
                p: '在桥接主机上放行入站 TCP 3128（代理）与 UDP 53535（发现）。自启用「开机触发 + 以 SYSTEM 运行」的计划任务，或 NSSM——不要用 `sc.exe create`，本程序不实现服务控制协议。',
              },
              {
                warn: '出口白名单必须同时覆盖后端主机**和**实际签名的 OSS 主机。只放行后端时探针照样通过，但大文件 PUT 会以 403 失败。统一用 `-check-egress` 确认，别靠猜。',
              },
              {
                ul: [
                  '每个连接关闭时写一行 JSON 日志（remote、target、上下行字节数、耗时），这是审计凭据——不要关闭。',
                  '`403 source address is not allowed`——客户端源地址不在 `acl.allow_cidrs`。',
                  '`502 upstream unreachable`——准入与白名单都通过了，是 relay 这台机器自己出不去。',
                ],
              },
            ],
          },
        ],
      },
      {
        title: '流程与速查',
        sections: [
          {
            id: 'workflow',
            no: '15',
            title: '端到端流程与分工',
            blocks: [
              {
                steps: [
                  '**准备**——安装 Scout、加入项目、签发 PIN，网络需要时部署 relay。责任：工程师 / 实验室 IT。',
                  '**采集**——配置崩溃转储类型；GUI 一键 Run scan 或等计划任务触发。责任：工程师。',
                  '**上传**——五步流水线，预签名直传 OSS。责任：Scout 自动完成。',
                  '**分析**——Worker 领取任务，用 CDB / WinDbg 取证。责任：平台自动完成。',
                  '**结论与归档**——复核结论、归并案例、分享只读链接、闭环关闭。责任：工程师 / 负责人。',
                ],
              },
              {
                table: {
                  head: ['角色', '负责'],
                  rows: [
                    ['工程师', '采集与上传、复核结论、归档与分享。'],
                    ['SA Leader', '成员与设置、成功/失败判定、关闭案例。'],
                    ['实验室 IT', 'relay 部署与出口白名单。'],
                    ['平台管理员', 'Worker、队列健康、账号与邮件通知。'],
                  ],
                },
              },
            ],
          },
          {
            id: 'glossary',
            no: '16',
            title: '术语与速查',
            blocks: [
              {
                table: {
                  head: ['术语', '英文', '含义'],
                  rows: [
                    ['事件', 'Event', '一次上传对应一条事件，含转储、附件与各次分析结果。'],
                    [
                      '分析运行',
                      'Analysis run',
                      '对同一事件的第 N 次分析，首次自动，之后可手动重跑。',
                    ],
                    ['案例', 'Case', '把多条同源事件归并成一个问题单跟踪。'],
                    ['设备', 'Device', '按 SMBIOS UUID / 序列号 / MAC 归并的机器资产。'],
                    ['蓝屏错误码', 'Bugcheck', '如 `0x9F` = DRIVER_POWER_STATE_FAILURE。'],
                    ['上传 PIN', 'Uploader PIN', '12 位凭据，供测试机免登录上传。'],
                    ['中继', 'Relay', '内网可出网机器上的 HTTP CONNECT 正向代理。'],
                    ['预签名 URL', 'Presigned URL', '后端签发、限时有效的直传地址。'],
                  ],
                },
              },
              {
                table: {
                  head: ['端口 / 路径', '用途'],
                  rows: [
                    ['443/tcp', '平台与 OSS 上传（HTTPS）——必须放行。'],
                    ['3128/tcp', 'relay 默认监听端口。'],
                    ['53535/udp', 'relay 局域网发现信标。'],
                    ['`bsod.lenovo.com`', '控制台；`/docs` 是本页，`/download` 是客户端下载。'],
                  ],
                },
              },
              {
                p: '本文档随产品版本更新。发现界面与描述不一致时，以平台上实际页面为准，并把差异反馈给平台管理员，以便纳入下一版修订。',
              },
            ],
          },
        ],
      },
    ],
  },
  download: {
    title: '下载',
    subtitle: '转储诊断客户端，仅支持 Windows x64。',
    empty: '暂未发布任何版本，请稍后再来。',
    load_error: '加载版本列表失败。',
    retry: '重试',
    version: '版本',
    platform: '平台',
    format: '格式',
    size: '大小',
    published: '发布时间',
    sha256: 'SHA-256',
    copied: '已复制',
    copy: '复制',
    download: '下载',
    kind_scout: '测试机图形客户端',
    kind_relay: '无外网测试机的局域网转发代理',
    view_docs: '第一次使用？查看平台介绍与快速开始',
    usage_title: '使用说明',
    usage_scout: '测试机用的 Windows 图形客户端：登录后一键采集上传，或安装开机任务：',
    usage_scout_code: 'BsodScout.exe -Login\nBsodScout.exe -InstallTask -ProjectId <项目ID>',
    usage_scout_relay_hint:
      '无法访问公网的测试机，经由桥接主机上的中继访问后端（bsod-relay.json）：',
    usage_scout_relay_code:
      '{\n  "listen": "0.0.0.0:3128",\n  "beacon": { "enabled": true, "port": 53535 },\n  "allow": {\n    "hosts": ["bsod.lenovo.com", "platform-bsod.oss-cn-hangzhou.aliyuncs.com"],\n    "ports": [443]\n  }\n}',
    usage_relay: '在能访问平台的桥接机上运行，测试机再指向它——relay 自身不需要配置账号或凭据：',
    usage_relay_code:
      'bsod-relay.exe -check -config bsod-relay.json\nbsod-relay.exe -config bsod-relay.json',
  },
  // /settings —— 控制台自身的外观。名字说的是"效果"不是"颜色"：选「蓝色」是在选
  // 链接和按钮之后会长成什么样，色块本身已经把颜色画出来了（spec §32）。
  settings: {
    subtitle: '控制台的外观与阅读方式。',
    theme: '主题',
    theme_hint: '跟随系统，或固定在一种。',
    accent: '强调色',
    accent_hint: '链接、按钮和当前页。',
    accent_note: '只保存在当前浏览器。状态色（成功、进行中、失败）不随之改变。',
    accent_red: '红',
    accent_orange: '橙',
    accent_amber: '琥珀',
    accent_green: '绿',
    accent_teal: '青',
    accent_blue: '蓝',
    accent_violet: '紫',
    accent_ink: '单色',
    language: '语言',
    language_hint: '仅影响界面文字。',
  },
  public: {
    nav_docs: '介绍',
    nav_download: '下载',
    sign_in: '登录',
    enter_console: '进入控制台',
    footer: '蓝屏转储采集与分析',
    // 页脚链接到自动生成的纯文本许可声明。
    license_toggle: '开源许可',
  },
  welcome: {
    eyebrow: '联想内部平台',
    title_lead: '从蓝屏转储',
    title_tail: '到明确的结论',
    lede: '从机群采集，自动符号解析，把故障驱动、模块与可能诱因收进同一条结论。',
    cta_start: '登录并开始',
    diagram_trace_label: '崩溃调用链',
    diagram_suspect: '可疑帧',
    diagram_capture_label: '从测试机到项目事件的采集路径',
    diagram_bench: '测试台',
    diagram_fleet: '实验室机群',
    diagram_bundle: '转储 + 日志',
    diagram_event: '项目事件',
    how_title: '从上传到结论，只有一条证据链',
    how_lede: '转储、调用栈和判断依据放在一起，下一个接手的人不用从头再来。',
    closing_line: '每个结论，都留得住原始转储、调用栈和故障模块。',
    example_label: '示例',
    example_title: '恢复时崩溃',
    example_cause_label: '根因',
    example_cause: '驱动在休眠时未能正常断电。',
    step_collect_title: '采集',
    step_collect_body: '测试台上一次点击即可抓取，也可在实验室每台机器上安装静默启动任务。',
    step_analyse_title: '解析',
    step_analyse_body: '每个转储都会做符号解析，定位出错调用栈、驱动版本与反复出现的模式。',
    step_verdict_title: '诊断',
    step_verdict_body: '给出 Bugcheck 码、可疑模块和一段简短结论，并附上支撑它的证据。',
  },
  auth: {
    heading: '登录',
    lede: '使用工作账号登录，或通过所属公司的单点登录继续。',
    email: '邮箱',
    password: '密码',
    submit: '登录',
    failed: '登录失败，请检查邮箱和密码。',
    or_email: '或用邮箱登录',
    email_option: '使用邮箱登录',
    email_hide: '收起邮箱登录',
    sso_with: '使用 {{name}} 登录',
    help: '登录遇到问题？请联系管理员。',
    captcha_required: '请先完成人机验证',
    captcha_failed: '验证码校验失败，请重试。',
  },
  nav: {
    open_menu: '打开菜单',
    home: '首页',
    pick_project_hint: '选择一个项目即可查看其导航。',
    switch_project: '切换项目',
    select_project: '选择项目',
    search_projects: '搜索项目',
    no_projects_match: '未找到匹配的项目',
    // 侧栏「事件」行上的计数：最近 24 小时、你可见的所有项目一共上传了多少
    recent_uploads: '最近 24 小时你的项目共上传 {{count}} 个事件',
    dashboard: '仪表盘',
    common_defects: '共性',
    events: '事件',
    cases: '案例',
    projects: '项目',
    all_projects: '所有项目',
    docs: '文档',
    download: '下载',
    settings: '设置',
    submit: '新建分析',
    devices: '设备',
    catalog: '产品目录',
    admin: '管理',
    account: '账号',
    language: '语言',
    theme: '主题',
    theme_light: '浅色',
    theme_dark: '深色',
    theme_system: '跟随系统',
    back_to_console: '返回转储诊断',
    role_pa: 'PA',
    role_engineer: '工程师',
    role_lead: 'SA Leader',
    role_admin: '管理员',
    role_super_admin: '超级管理员',
  },
  command: {
    trigger: '搜索',
    hint: '跳转到页面或项目，或搜索事件。',
    placeholder: '搜索事件、页面和项目…',
    empty: '没有匹配的结果。',
    search_group: '搜索',
    search_events: '搜索事件："{{query}}"',
    navigation: '导航',
    actions: '操作',
    projects: '项目',
    theme: '主题',
    links: '链接',
  },
  errors: {
    auth_token_invalid: '会话已过期，请重新登录。',
    rbac_forbidden: '你没有执行此操作的权限。',
    not_found: '未找到。',
    network: '网络错误，请检查连接。',
    unknown: '未知错误。',
  },
  pages: {
    dashboard_title: '仪表盘',
    common_defects_title: '共性',
    events_title: '事件',
    cases_title: '案例',
    projects_title: '项目',
    submit_title: '新建分析',
    account_title: '账号',
    settings_title: '设置',
    not_found_title: '页面未找到',
    not_found_body: '你访问的页面不存在。',
    coming_soon: '即将上线',
  },
  projects: {
    list: {
      new_project: '新建项目',
      no_projects_title: '暂无项目',
      no_projects_can_create: '创建一个项目开始使用。',
      no_projects_no_perm: '请联系管理员把你加入项目。',
      col_bios_prefixes: 'BIOS 前缀',
      col_updated: '更新时间',
      row_actions: '{{name}} 的操作',
      row_edit: '编辑',
      row_members: '成员',
      modal_title: '新建项目',
      name_label: '名称',
      name_placeholder: 'Workstation 7000',
      description_label: '描述',
      description_placeholder: '可选',
      owner_company_label: '所属公司',
      owner_company_placeholder: '选择公司',
      lead_label: 'SA Leader',
      lead_placeholder: '选择 SA Leader',
      lead_search: '按姓名或邮箱搜索',
      lead_empty: '没有匹配的用户',
      lead_required: '请选择 SA Leader',
      members_label: '成员',
      members_hint: '可选。每个人会按你选择的角色加入项目。',
      member_add: '添加成员',
      member_placeholder: '选择成员',
      member_remove: '移除成员',
      member_required: '请为每个成员选择用户',
      tab_active: '活跃',
      tab_archived: '已归档',
      search_placeholder: '搜索项目…',
      subtitle: '{{count}} 个项目',
      subtitle_with_company: '{{count}} 个项目（{{company}}）',
    },
    overview: {
      metric_recent: '近期事件',
      recent_events: '近期事件',
      recent_cases: '近期案例',
      no_events: '暂无事件',
      no_cases: '暂无案例',
      no_cases_desc: '创建案例以跟踪问题。',
      facts: '项目概况',
      fact_devices: '设备',
      fact_members: '成员',
      fact_public_id: '编号',
      fact_created: '创建于',
    },
    members: {
      title: '成员',
      subtitle:
        'PA 可查看、上传转储、发表评论并编辑事件描述；工程师可额外编辑案例；SA Leader 可管理事件、成员与项目设置。',
      add: '添加成员',
      modal_title: '添加成员',
      col_name: '姓名',
      col_email: '邮箱',
      col_role: '角色',
      col_joined: '加入时间',
      role_pa: 'PA',
      role_engineer: '工程师',
      role_lead: 'SA Leader',
      confirm_change_role: '将角色改为 {{role}}？',
      confirm_remove: '移除 {{name}}？',
      empty: '暂无成员',
      email_placeholder: 'engineer@bsod.test',
      search_placeholder: '按姓名或邮箱搜索',
      no_match: '无匹配成员',
    },
    member_groups: {
      title: '用户组',
      subtitle: '把常用成员保存成组，之后可在任意项目中一键批量添加。',
      page_subtitle: '把常用成员保存成公司级模板，可应用到你有权限管理的任意项目。',
      manage: '管理用户组',
      new_group: '新建用户组',
      new_title: '新建用户组',
      edit_title: '编辑用户组',
      editor_hint: '用户组是可复用的快捷方式；修改只会影响之后添加的成员。',
      name: '组名称',
      name_placeholder: '例如：平台团队',
      role: '项目角色',
      role_for: '{{name}} 的项目角色',
      selected: '已选成员',
      add_member: '添加成员',
      search_members: '搜索公司用户',
      no_members_selected: '尚未选择成员',
      no_candidates: '没有匹配的用户',
      no_members_yet: '暂无可用成员',
      no_groups: '还没有用户组',
      no_groups_desc: '创建一个用户组，之后可在本公司的任意项目中复用。',
      none_for_add: '本公司还没有可用的用户组。',
      load_failed: '加载用户组失败',
      total: '{{count}} 个用户组',
      total_one: '{{count}} 个用户组',
      total_other: '{{count}} 个用户组',
      member_count_one: '{{count}} 名成员',
      member_count_other: '{{count}} 名成员',
      name_required: '请输入组名称。',
      members_required: '请至少添加一名成员。',
      save: '保存用户组',
      created: '用户组已创建',
      updated: '用户组已更新',
      deleted: '用户组已删除',
      save_failed: '保存用户组失败',
      apply_title: '应用用户组到项目',
      apply_desc: '选择需要补充当前成员的项目；已经在项目中的成员会保留现有角色。',
      apply_selected: '已选 {{count}} 个项目',
      apply_select_all: '全选',
      apply_no_projects: '没有可管理的项目',
      apply_skip: '暂不应用',
      apply_action: '应用到 {{count}} 个项目',
      apply_action_one: '应用到 1 个项目',
      apply_to_projects: '应用到项目',
      company_scope_hint: '公司内所有项目均可复用',
      apply_choose: '请至少选择一个项目。',
      apply_success: '已更新 {{projects}} 个项目 · 添加 {{added}} 人 · 跳过 {{skipped}} 人',
      apply_partial: '已更新 {{projects}} 个项目 · {{failed}} 个失败',
      apply_failed: '部分项目应用失败',
      edit: '编辑 {{name}}',
      delete: '删除 {{name}}',
      remove_member: '移除 {{name}}',
      delete_title: '删除 {{name}}？',
      delete_desc: '已经通过这些成员加入的项目不会受到影响。',
      person_tab: '单个用户',
      group_tab: '从用户组添加',
      group: '用户组',
      choose_group: '选择用户组',
      choose_group_error: '请选择用户组。',
      add_hint: '会按组内每个成员各自的项目角色添加；已经在项目中的成员会自动跳过。',
      add_group: '添加用户组',
      added_to_project: '已添加 {{added}} 人 · 跳过 {{skipped}} 人',
      add_failed: '添加用户组失败。',
    },
    settings: {
      title: '项目设置',
      subtitle: '这个项目的名称、BIOS 前缀与生命周期。',
      public_id_prefix: '编号',
      identity: '基本信息',
      name_label: '名称',
      description_label: '描述',
      save: '保存',
      save_changes: '保存更改',
      saved: '已保存',
      lifecycle: '生命周期',
      archived_msg: '该项目已归档，默认列表不可见，但数据仍可访问。',
      active_msg: '该项目处于活跃状态并正在接收转储文件。',
      archive: '归档',
      unarchive: '取消归档',
      confirm_archive: '归档该项目？',
      danger_zone: '危险操作',
      danger_msg: '删除不可恢复。所有事件、案例、设备、成员都会被移除。',
      delete: '删除项目',
      confirm_delete_title: '删除项目 {{name}}？',
      confirm_delete_desc: '此操作不可撤销。',
      bios_prefixes_label: 'BIOS 前缀',
      bios_prefixes_hint: '当本机 BIOS 版本以其中某个前缀开头时，Scout 自动选用本项目。',
      bios_prefixes_save_failed: '保存 BIOS 前缀失败',
    },
    devices: {
      title: '设备',
      subtitle: '曾向该项目上传过 dump 的设备。',
      col_hostname: '主机名',
      col_mac: 'MAC',
      col_serial: '序列号',
      col_model: '机型',
      col_sku: 'SKU',
      col_last_seen: '最近活动',
      unknown_model: '未知机型',
      unknown_sku: '未知 SKU',
      never: '从未',
      empty: '暂无设备',
      search_placeholder: '按主机名、MAC、序列号、机型或 SKU 搜索',
      no_match: '无匹配设备',
    },
    catalog: {
      title: '产品目录',
      subtitle: '机型归类 SKU；每台设备绑定到一个 SKU。',
      new_model: '新建机型',
      edit_model: '编辑机型',
      confirm_delete_model: '删除该机型及其全部 SKU？',
      new_sku: '新建 SKU',
      edit_sku: '编辑 SKU',
      confirm_delete_sku: '删除 SKU {{code}}？',
      edit: '编辑',
      col_updated: '更新时间',
      models_count: '机型',
      models_count_with: '机型（{{count}}）',
      skus_for: '{{name}} 的 SKU',
      select_model_prompt: '选择机型',
      pick_model_left: '在左侧选择一个机型',
      no_models: '暂无机型',
      no_skus: '暂无 SKU',
      back_to_models: '返回机型',
      name_label: '名称',
      description_label: '描述',
      code_label: '编码',
      model_name_placeholder: 'ThinkPad X1 Carbon',
      sku_code_placeholder: '20U9-CTO1WW',
      description_placeholder: '可选',
    },
  },
  submit: {
    description_label: '描述',
    description_placeholder: '描述发生了什么（可选）',
    project_label: '项目',
    project_placeholder: '选择项目',
    file_label: 'Dump 文件（.zip）',
    file_hint: '拖拽 .zip 转储文件到此处',
    file_hash_hint: '上传前在本地计算 SHA-256。',
    choose_file: '选择文件',
    start: '开始上传',
    hashing: '本地计算哈希…',
    uploading: '上传至 OSS…',
    finalizing: '注册事件…',
    done: '完成，正在跳转…',
    failed_msg: '上传失败。',
    subtitle: '上传 Windows 崩溃转储文件（.zip），解析 Bugcheck 并生成 AI 根因分析。',
    file_zip_only: '仅支持 .zip 上传，大文件将使用分片上传。',
    zip_only_error: '这不是 .zip 文件，请将转储打包为 .zip 后上传。',
    tls_note: '已哈希并通过 TLS 上传',
    desc_optional: '· 可选',
    replace: '替换',
    remove: '移除',
    sha_verified: '· SHA-256 已本地验证',
    project_hint: '转储会成为该项目下的一条「事件」。',
    description_hint: '崩溃前发生了什么——有助于生成报告。',
    need_project: '先选择项目',
    need_file: '先选择 .zip 转储文件',
  },
  account: {
    profile: '个人信息',
    password: '修改密码',
    memberships: '项目成员资格',
    email: '邮箱',
    company: '公司',
    roles: '角色',
    display_name: '显示名称',
    save: '保存',
    updated: '已更新',
    password_changed: '密码已修改',
    profile_saved: '信息已更新',
    change_avatar: '更换头像',
    add_avatar: '添加头像',
    remove_avatar: '移除头像',
    avatar_updated: '头像已更新',
    avatar_removed: '头像已移除',
    avatar_update_failed: '头像更新失败',
    avatar_remove_failed: '头像移除失败',
    avatar_type_error: '请选择 PNG、JPEG 或 WebP 图片。',
    avatar_source_size_error: '请选择小于 10 MB 的图片。',
    avatar_output_size_error: '图片压缩后仍然过大。',
    avatar_process_error: '无法处理这张图片。',
    avatar_crop_title: '裁切头像',
    avatar_crop_description: '拖动图片调整位置，并缩放取景范围。',
    avatar_crop_preview: '头像裁切预览',
    avatar_crop_zoom: '缩放',
    avatar_crop_reset: '重置',
    avatar_crop_apply: '使用照片',
    no_projects: '你尚未加入任何项目。',
    events_arrow: '事件 →',
    current_password: '当前密码',
    new_password: '新密码',
    confirm_password: '确认新密码',
    password_min_12: '至少 12 个字符',
    passwords_mismatch: '两次密码不一致',
    role_super_admin: '超级管理员',
    role_company_admin: '公司管理员',
    role_member: '成员',
    read_only: '只读',
    password_note: '最少 12 个字符。此设备上的登录状态将保持不变。',
    strength_weak: '弱',
    strength_fair: '一般',
    strength_good: '良好',
    strength_strong: '强',
  },
  share: {
    invalid_or_expired: '此链接已失效或过期',
    untitled: '未命名分析',
    status_label: '状态',
    uploaded_label: '上传于',
    files_heading: '文件',
    download: '下载',
    expires_at: '此链接将于',
    toggle_zh: '中文',
    toggle_en: 'EN',
  },
  device: {
    identity: '设备标识',
    telemetry: '遥测',
    catalog_assignment: '产品目录分配',
    edit: '编辑',
    save_assignment: '保存分配',
    clear: '清除',
    model_label: '机型',
    sku_label: 'SKU',
    select_model: '选择机型',
    select_sku: '选择 SKU',
    pick_model_first: '请先选择机型',
    hostname_label: '主机名',
    serial_label: '序列号',
    smbios_uuid_label: 'SMBIOS UUID',
    primary_mac_label: '主 MAC',
    all_macs_label: '所有 MAC',
    device_id_label: '设备 ID',
    created_label: '创建于',
    updated_label: '更新于',
    last_seen_label: '最近活动',
    last_event_label: '最近事件',
    never: '从未',
    edit_title: '编辑设备',
    edit_hostname: '主机名',
    edit_mac: 'MAC 地址',
    edit_serial: '序列号',
    catalog_saved: '产品目录分配已保存',
    catalog_cleared: '产品目录分配已清除',
    last_seen_at: '最近活动 {{time}}',
    never_seen: '从未活动',
    pick_both_or_clear: '请同时选择机型和 SKU，或使用"清除"移除分配。',
    unknown_model: '未知机型',
    unknown_sku: '未知 SKU',
  },
  pending: {
    title: '账号待审批',
    subtitle_with_email: '你使用 {{email}} 的登录申请尚未激活。',
    subtitle: '你的登录申请正在等待管理员审批。',
    sign_in_again: '重新登录',
    body: '管理员已收到通知。账号审批通过后，你将通过邮件或内部渠道获知。',
  },
  dashboard: {
    greeting_morning: '早上好',
    greeting_afternoon: '下午好',
    greeting_evening: '晚上好',
    metric_recent_events: '近期事件',
    metric_analyzing_now: '正在分析',
    metric_success_24h: '成功 · 24h',
    metric_failed_24h: '失败 · 24h',
    events_per_day: '每日事件数',
    top_bugchecks: '常见错误码',
    no_project_title: '未选择项目',
    no_project_desc: '请从右上角的项目切换器中选择一个项目。',
    recent_activity: '最近动态',
    view_all: '查看全部 →',
    live: '实时',
    range_7d: '7天',
    range_30d: '30天',
    range_90d: '90天',
    range_all: '全部',
    no_events_title: '暂无事件',
    no_events_desc: '提交一个 dump 文件即可开始。',
    dump_singular: '个 dump',
    dumps_plural: '个 dump',
    landed_last_hour: '在最近一小时内上传',
    delta_new: '新',
  },
  overview: {
    subtitle: '你可见的 {{count}} 个项目的上传总览',
    metric_events: '事件 · 近 {{days}} 天',
    recent_events: '最新事件',
    recent_cases: '最近更新的案例',
  },
  common_defects: {
    feature_name: '共性问题',
    fact_qualifier: 'Common Defect Detection',
    subtitle: '共性问题 · 跨项目重复签名：{{count}} 个',
    empty_title: '未发现共性问题',
    empty_desc: '同一个 bucket 签名在至少两个项目中出现时，才会进入这里。',
    col_signature: '签名',
    col_module: '故障模块',
    col_projects: '项目数',
    col_events: '事件数',
    col_cases: '案例数',
    col_last_seen: '最近出现',
    col_project: '项目',
    col_event: '事件',
    col_title: '标题',
    col_case: '案例',
    col_status: '状态',
    col_uploaded: '上传时间',
    fact_module: '故障模块',
    fact_first_seen: '首次出现',
    fact_last_seen: '最近出现',
    metric_projects: '项目',
    metric_events: '事件数',
    metric_cases: '案例数',
    metric_accessible: '可查看',
    affected_projects: '受影响项目',
    visible_subset: '仅显示你有权限访问的项目',
    no_access_title: '没有可查看的项目证据',
    no_access_desc: '你可以查看聚合信息，但包含该问题的项目目前都不在你的访问范围内。',
    recent_events: '最近事件',
    events_hidden: '显示 {{shown}} / {{total}}',
    events_limited: '仅显示最近 {{count}} 条',
    no_events_title: '没有可查看的事件',
    no_events_desc: '你没有权限访问的项目中的事件不会显示。',
    panel_description: '共性问题 · 该崩溃签名的跨项目证据',
    not_detected_title: '未检测到共性问题',
    not_detected_desc: '该签名在当前时间窗口内尚未跨多个项目重复出现。',
  },
  events: {
    recent_upload_hint: '过去 24 小时内上传',
    new_badge: '新',
    common_defect_badge: '共性',
    common_defect_badge_hint: '跨 {{count}} 个项目重复出现',
    row_summary: '崩溃摘要',
    row_facts: '事件信息',
    row_common_defects: '共性',
    common_defect_loading: '正在查询共性问题…',
    common_defect_summary: '项目 {{projects}} · 事件 {{events}} · 案例 {{cases}}',
    common_defect_visible: '可查看 {{visible}} / {{total}} 个项目',
    common_defect_not_detected: '未发现跨项目重复出现',
    common_defect_failed: '共性问题查询失败',
    compare_selected: '已选 {{count}} 个',
    compare_hint: '至少选择 2 个事件进行对比。',
    compare_launch: '对比所选',
    compare_title: '对比事件',
    compare_description: '{{count}} 个事件 · 关键分析与机器信息',
    compare_field: '字段',
    compare_only_diff: '仅看差异',
    compare_no_fields: '这些事件没有可对比的字段。',
    compare_no_differences: '这些字段没有差异。',
    compare_differs: '有差异',
    compare_probably_caused_by: '可能原因',
    compare_crash_frame: '栈顶帧',
    compare_bugcheck_parameters: 'Bugcheck 参数',
    compare_os: '系统',
    compare_machine: '机器',
    compare_memory: '内存',
    compare_memory_value: '{{total}} 总量 · {{available}} 可用',
    compare_memory_available: '{{available}} 可用',
    compare_cpu: '处理器',
    compare_threads: '{{count}} 线程',
    select_project_title: '选择项目',
    select_project_hint: '从右上角的项目切换器选择一个项目以查看其事件。',
    search_placeholder: '搜索或 key:value（如 bugcheck:0xa0 status:success）',
    col_project: '项目',
    col_status: '状态',
    col_event_id: '事件 ID',
    col_bugcheck: 'Bugcheck',
    col_title: '标题',
    col_uploaded: '上传时间',
    col_description: '描述',
    col_faulting_module: '故障模块',
    col_bucket_id: '分组 ID',
    col_bios: 'BIOS',
    col_uploader: '上传者',
    col_case: '关联案例',
    open_case: '查看案例',
    case_untitled: '未命名案例',
    related_events_failed: '关联事件加载失败',
    col_tags: '标签',
    no_match_title: '未找到匹配的事件',
    no_match_filters: '没有符合该状态的事件',
    no_events_title: '暂无事件',
    no_events_desc: '提交一个 dump 文件即可开始。',
    description_add: '添加描述',
    description_edit: '编辑描述',
    description_placeholder: '添加描述…',
    description_save_failed: '描述保存失败',
  },
  cases: {
    select_project_title: '选择项目',
    select_project_hint: '从右上角的项目切换器选择一个项目以查看其案例。',
    all_statuses: '全部',
    status_open: '进行中',
    status_closed: '已关闭',
    search_placeholder: '搜索或 key:value（如 status:open creator:worker）',
    col_project: '项目',
    col_status: '状态',
    col_case_id: '案例 ID',
    col_title: '标题',
    col_events: '事件数',
    col_created: '创建时间',
    col_updated: '更新时间',
    no_match_title: '未找到匹配的案例',
    no_cases_title: '暂无案例',
    no_cases_desc: '案例由 Worker 自动创建。',
  },
  case_detail: {
    back_to_cases: '返回案例',
    tab_description: '描述',
    tab_comments: '评论',
    tab_links: '外部链接',
    edit: '编辑',
    close_case: '关闭案例',
    reopen_case: '重新开启',
    confirm_close: '关闭该案例？',
    confirm_reopen: '重新开启该案例？',
    rail_title: '案例',
    fact_status: '状态',
    fact_id: '编号',
    fact_created: '创建于',
    fact_events: '事件数',
    fact_signature: '签名',
    linked_events: '关联事件',
    no_events_linked: '暂无关联事件',
    no_description: '暂无描述',
    no_comments: '暂无评论',
    no_links: '暂无链接',
    post_comment: '发布',
    comment_placeholder: '写一条 Markdown 评论…',
    comment_hint: '支持 Markdown · Ctrl/⌘+Enter 发送',
    link_url_placeholder: 'https://…',
    link_title_placeholder: '标题（可选）',
    add_link: '添加',
    delete_comment: '删除评论？',
    delete_link: '删除链接？',
    edit_case: '编辑案例',
    case_title_label: '标题',
    description_label: '描述（Markdown）',
  },
  admin: {
    admin_mode: '管理模式',
    admin_section: '管理',
    users_title: '用户',
    users_subtitle: '管理用户账号、角色和访问权限。',
    member_groups_title: '用户组',
    users_tab_active: '活跃',
    users_tab_pending: '待审批',
    users_filter_company: '按公司筛选',
    users_new: '新建用户',
    users_col_email: '邮箱',
    users_col_name: '姓名',
    users_col_role: '角色',
    users_col_status: '状态',
    users_col_created: '创建时间',
    users_role_super_admin: '超级管理员',
    users_role_company_admin: '公司管理员',
    users_role_user: '用户',
    users_status_active: '活跃',
    users_status_disabled: '已停用',
    users_empty: '暂无用户',
    pending_approve: '批准',
    pending_reject: '拒绝',
    pending_col_email: '邮箱',
    pending_col_domain: '域名',
    pending_col_provider: '提供商',
    pending_col_requested: '首次申请',
    pending_col_retries: '重试次数',
    companies_title: '公司',
    companies_subtitle: '租户边界。每个用户、项目和事件都属于一家公司。',
    companies_new: '新建公司',
    companies_col_name: '名称',
    companies_col_slug: '标识符',
    companies_col_domains: '邮箱域名',
    companies_col_created: '创建时间',
    companies_empty: '暂无公司',
    workers_title: '分析工作器',
    workers_subtitle: '从队列消费任务的分析工作器。',
    workers_add: '新增工作器',
    workers_add_title: '新增工作器',
    workers_add_desc: '一步创建该工作器及其密钥，并生成在其主机上运行的命令。',
    workers_add_submit: '创建工作器',
    worker_rename: '重命名',
    worker_rename_title: '重命名 {{name}}',
    worker_rename_desc: '只改显示名 —— 机器上持有的密钥继续有效，状态也不会被改动。',
    worker_rename_submit: '保存名称',
    workers_renamed: '工作器已重命名',
    workers_rename_failed: '重命名失败',
    workers_created: '工作器已创建',
    workers_create_failed: '创建工作器失败',
    worker_name_label: '工作器名称',
    worker_region_label: '地区',
    worker_id_label: '工作器 ID',
    worker_created_title: '工作器 {{name}} 已创建',
    worker_created_desc:
      '在目标主机上运行下面这条命令即可写入密钥，不需要再做别的。密钥只显示这一次。',
    worker_command_label: '在目标主机上运行（PowerShell）',
    worker_key_label: '工作器密钥',
    worker_copy_command: '复制命令',
    worker_copy_key: '复制密钥',
    worker_copied: '已复制到剪贴板',
    worker_created_hint:
      '它现在在列表里显示为「已退出」，首次上报后转为在线 — 随后用 `bsod-worker run` 启动主循环。',
    workers_active_only: '仅活跃',
    workers_col_name: '名称',
    workers_col_region: '地区',
    workers_col_version: '版本',
    workers_col_last_seen: '最近活动',
    workers_empty: '暂无工作器',
    workers_col_runtime: '状态',
    workers_col_ip: '公网 IP',
    workers_col_current_event: '当前 event',
    workers_col_health: '健康度',
    worker_rt_busy: '运行中',
    worker_rt_idle: '空闲',
    worker_rt_offline: '已退出',
    worker_rt_revoked: '已吊销',
    worker_rt_paused: '已暂停',
    worker_health_ok: '正常',
    worker_health_degraded: '降级',
    worker_health_model_down: '模型不可用',
    worker_pause: '暂停接单',
    worker_resume: '恢复接单',
    worker_revoke: '吊销',
    worker_pause_confirm: '暂停 {{name}}？正在处理的任务会被中止并重新入队。',
    // Relays
    relays_title: '中继',
    relays_subtitle: '局域网出口中继，数据来自它们自己的心跳上报。',
    relays_online: '在线',
    relays_active_connections: '活动连接',
    relays_egress_down: '出口异常',
    relays_transferred: '累计流量',
    relays_dial_failures: '上游失败',
    relays_col_name: '中继',
    relays_col_owner: '负责人',
    relays_col_status: '状态',
    relays_col_egress: '出口',
    relays_col_listen: '监听',
    relays_col_version: '版本',
    relays_col_active: '活动',
    relays_col_traffic: '流量',
    relays_col_errors: '错误',
    relays_col_last_seen: '最近上报',
    relays_no_errors: '启动以来无错误',
    relays_add: '新增中继',
    relays_add_title: '新增中继',
    relays_add_desc:
      '按它所在的位置命名 —— 这是列表里区分多台中继的唯一依据 —— 并写上为它负责的人。',
    relays_add_submit: '新增中继',
    relay_name_label: '中继名称',
    relays_created: '中继已新增',
    relays_create_failed: '新增中继失败',
    relay_created_title: '中继 {{name}} 已新增',
    relay_created_desc: '把它填进中继主机的配置并重启。密钥只显示这一次，之后随时可以在这里吊销。',
    relay_created_hint: '第一次心跳前它会以「离线」出现在列表里，收到心跳后 30 秒内转为「在线」。',
    relays_id_label: '中继 ID',
    relay_status_config_label: 'bsod-relay.json 里的 status 段',
    relay_copy_config: '复制配置',
    relays_key_label: '中继密钥',
    relays_copy_key: '复制密钥',
    relays_active_only: '隐藏已吊销',
    relays_revoke: '吊销密钥',
    relays_revoke_confirm: '吊销 {{name}} 的密钥？',
    relays_revoke_desc:
      '中继仍会继续代理，但下一次心跳会被拒绝，并从此在控制台消失。用同一个名字再新增一次即可签发新密钥。',
    relays_edit: '编辑',
    relay_edit_title: '编辑 {{name}}',
    relay_edit_desc:
      '只改控制台自己记的东西 —— 显示名和负责人。密钥继续有效，计数器也保留。要换一把新密钥，请用这个名字重新新增中继。',
    relay_edit_submit: '保存',
    relays_updated: '中继已更新',
    relays_update_failed: '更新中继失败',
    relays_name_conflict: '已有另一台中继使用该名称',
    relay_owner_label: '负责人',
    relay_owner_none: '未指派',
    relay_owner_pick: '选择负责人',
    relay_owner_search: '搜索姓名或邮箱',
    relay_owner_empty: '没有匹配的用户',
    relays_owner_required: '请选择负责人',
    relay_rt_revoked: '已吊销',
    relays_empty: '暂无中继',
    relays_empty_hint:
      '中继首次心跳后就会出现在这里。点上方按钮新增一台，再把生成的 `status` 配置粘贴进它的配置文件即可。',
    relay_rt_online: '在线',
    relay_rt_offline: '离线',
    relay_egress_ok: '可达',
    relay_egress_degraded: '不可达',
    relay_egress_unknown: '未知',
    relay_detail_client_acl: '客户端 ACL',
    relay_detail_ports: '出口端口',
    relay_detail_beacon: '发现应答',
    relay_detail_beacon_on: '开启，UDP {{port}}',
    relay_detail_beacon_off: '关闭',
    relay_detail_connections: '连接数',
    relay_detail_connections_value: '{{active}} 活动 · 共 {{total}}',
    relay_detail_rejects: '被拒（ACL / 白名单）',
    relay_detail_started: '启动于',
    relay_detail_offline_alert: '上次离线提醒',
    relay_detail_offline_alert_none: '未发送',
    relay_detail_last_error: '最近错误',
    relay_detail_egress_checked: '出口自检 · {{when}}',
    relay_detail_egress_pending: '出口自检：尚未运行',
    relay_detail_egress_failed: '{{count}} 条失败',
    relay_detail_no_targets: '暂无出口自检结果。',
    pi_creds_title: 'PI 凭证',
    pi_creds_subtitle: '分析工作器使用的提供商凭证。',
    pi_creds_new: '新建凭证',
    pi_creds_col_label: '标签',
    pi_creds_col_protocol: '协议',
    pi_creds_col_model: '模型',
    pi_creds_col_endpoint: '端点',
    pi_creds_col_active: '活跃',
    pi_creds_col_rotated: '轮换时间',
    pi_creds_active_yes: '是',
    pi_creds_active_no: '否',
    pi_creds_empty: '暂无凭证',
    pi_creds_edit: '编辑',
    pi_creds_delete: '删除',
    pi_creds_activate: '设为启用',
    pi_creds_activate_confirm: '将此凭证设为启用？',
    pi_creds_activate_desc: '当前启用中的凭证将被停用。',
    pi_creds_delete_confirm: '删除此凭证？',
    pi_creds_delete_active_desc: '这是当前启用中的凭证——在启用其它凭证前，worker 将没有可用凭证。',
    // Mail notifications
    mail_title: '邮件通知',
    mail_subtitle: '分析完成后，通过该 EWS 邮箱通知项目 SA Leader。',
    mail_ews_title: 'Exchange（EWS）',
    mail_ews_desc: '本地 Exchange 的域账号通常使用 NTLM 认证。密码加密存储，保存后不再回显。',
    mail_enabled: '启用通知',
    mail_enabled_desc: '关闭后分析仍会完成，但不会发信；下方配置会保留。',
    mail_host: 'EWS 主机',
    mail_host_desc: 'Exchange 服务器 FQDN；填写下方完整 URL 时此项被忽略。',
    mail_url: 'EWS URL（可选）',
    mail_url_desc: '仅在端口或路径非默认时需要填写。',
    mail_mailbox: '发件邮箱（From）',
    mail_username: '登录名',
    mail_domain: '域（可选）',
    mail_domain_desc: '留空则从登录名推断（域名\\用户 或 用户@域名）。',
    mail_password: '密码',
    mail_password_keep: '••••••••（留空表示不修改）',
    mail_auth: '认证方式',
    mail_verify_tls: '校验 TLS 证书',
    mail_verify_tls_desc: '仅当后端不信任内部证书时关闭。',
    mail_language: '邮件语言',
    mail_updated_at: '最后更新 {{when}}',
    mail_never_saved: '尚未配置',
    mail_test_title: '发送测试邮件',
    mail_test_desc: '使用已保存的配置发送，因此请先保存。通知开关关闭时也可测试。',
    mail_test_to: '收件人',
    mail_test_send: '发送测试',
    mail_test_sent: '测试邮件已发送，请查看收件箱。',
    mail_saved: '邮件配置已保存',
    mail_err_host: '请填写 Exchange 主机或完整的 EWS URL。',
    mail_err_mailbox: '请填写有效的邮箱地址。',
    mail_err_username: '请填写登录名。',
    mail_err_password: '首次配置必须填写密码。',
    audit_logs_title: '审计日志',
    audit_logs_subtitle: '管理和数据修改操作的只追加记录。',
    audit_logs_filter_entity: '按实体类型筛选（如 user）',
    audit_logs_filter_action: '按操作筛选（如 user.disable）',
    audit_logs_col_when: '时间',
    audit_logs_col_actor: '操作者',
    audit_logs_col_action: '操作',
    audit_logs_col_entity: '实体',
    audit_logs_col_entity_id: '实体 ID',
    audit_logs_col_project_id: '项目 ID',
    audit_logs_col_project: '项目',
    audit_logs_empty: '暂无审计日志',
    audit_logs_actor_worker: 'Worker',
    audit_logs_actor_system: '系统',
    audit_logs_entity_user: '用户',
    audit_logs_entity_project: '项目',
    audit_logs_entity_case: '案例',
    audit_logs_entity_event: '事件',
    audit_logs_entity_company: '公司',
    audit_logs_entity_device: '设备',
    audit_logs_entity_pending_user: '待审批用户',
    audit_logs_entity_task: '任务',
    audit_logs_entity_upload_pin: '上传 PIN',
    audit_logs_entity_pi_credential: 'PI 凭据',
    audit_logs_action_case_auto_create: '自动创建案例',
    audit_logs_action_case_reopen_by_worker: 'Worker 自动重新打开案例',
    audit_logs_action_pending_user_approve: '通过待审批用户',
    audit_logs_action_pending_user_reject: '拒绝待审批用户',
    audit_logs_action_task_cancel: '取消任务',
    audit_logs_action_task_restore: '恢复任务',
    audit_logs_action_upload_pin_delete: '删除上传 PIN',
    audit_logs_action_upload_pin_revoke: '撤销上传 PIN',
    audit_logs_action_device_catalog_assign: '分配设备目录项',
    audit_logs_action_device_catalog_reassign: '更换设备目录项',
    audit_logs_action_device_catalog_unassign: '移除设备目录项',
    queue_health_title: '队列健康',
    queue_health_subtitle: '分析任务队列状态和死信检查。',
    queue_health_pending: '待处理',
    queue_health_claimed: '处理中',
    queue_health_done: '已完成',
    queue_health_failed: '失败',
    queue_health_dead: '死信',
    queue_health_cancelled: '已取消',
    queue_health_recent: '近期任务',
    queue_health_requeue: '重新入队',
    queue_health_requeue_confirm: '重新入队该任务？',
    queue_health_cancel: '取消任务',
    queue_health_cancel_confirm: '取消该任务？记录会保留以便追溯，且可以恢复。',
    queue_health_cancel_pending_note: '该事件将不再有排队任务，因此会被标记为失败。',
    queue_health_restore: '恢复为待处理',
    queue_health_restore_confirm: '将该任务恢复为待处理并交给 worker 领取？',
    queue_health_col_event: '事件',
    queue_health_col_status: '状态',
    queue_health_col_attempts: '尝试次数',
    queue_health_col_last_error: '最近错误',
    queue_health_col_updated: '更新时间',
    queue_health_empty: '暂无任务',
    oidc_title: 'OIDC 提供商',
    // Releases (client binaries)
    releases_title: '版本发布',
    releases_subtitle: '发布客户端二进制。下载需要登录账号。',
    releases_empty: '暂无版本',
    release_new: '上传新版本',
    release_status: '状态',
    release_kind: '类型',
    release_version: '版本号',
    release_version_required: '请填写版本号',
    release_version_invalid: '使用语义化版本，例如 1.2.0',
    release_identity_locked: '创建草稿后不可修改——它是产物文件名的一部分；要改只能删掉草稿重建。',
    release_channel: '通道',
    release_size: '大小',
    release_sha256: 'SHA-256',
    release_created: '创建时间',
    release_published_at: '发布时间',
    release_actions: '操作',
    release_edit: '编辑',
    release_publish: '发布',
    release_yank: '下架',
    release_restore: '恢复发布',
    release_confirm_delete: '删除这个草稿？',
    release_notes: '发布说明',
    release_notes_placeholder: '本版本有哪些改动',
    release_notes_hint: '给内部看的变更记录；下载页不显示——下载页显示的是下面的「使用说明」。',
    release_usage: '使用说明',
    release_usage_placeholder: '这个版本怎么用；支持 Markdown，命令或配置用三个反引号包起来。',
    release_usage_hint: '下载页「使用说明」里显示的内容。留空则用内置的默认文案。',
    release_usage_builtin: '留空时读者看到的文案',
    release_usage_not_saved:
      '服务器没有保存使用说明（后端可能还是旧版本、不认识这个字段）——重启或重新部署后端后再试。',
    release_step_meta: '填写信息',
    release_step_upload: '上传',
    release_step_verify: '校验',
    release_file: '产物文件',
    release_file_hint: '直接上传到对象存储——应用服务器不经手文件内容。',
    release_choose_file: '选择或拖入要发布的产物（.exe / .zip / .7z）',
    release_file_mismatch: '文件名要与类型和版本号一致，期望',
    release_upload_failed: '上传失败',
    release_publish_now: '校验并发布',
    release_save_draft: '存为草稿',
    release_save: '保存',
    release_cancel: '取消',
    release_updated: '已更新',
    release_action_failed: '操作失败',
    release_deleted: '草稿已删除',
    release_published: '已发布',
    release_saved_draft: '已存为草稿',
    release_yanked: '已下架',
    oidc_subtitle: '管理用于 SSO 登录的 OpenID Connect 身份提供商。',
    oidc_add: '添加提供商',
    oidc_col_slug: '标识符',
    oidc_col_display_name: '显示名称',
    oidc_col_issuer: '签发方',
    oidc_col_client_id: '客户端 ID',
    oidc_col_enabled: '是否启用',
    oidc_status_enabled: '已启用',
    oidc_status_disabled: '已停用',
    oidc_empty: '暂无 OIDC 提供商',
    never: '从未',
    token_done: '完成',
  },
  event_detail: {
    breadcrumb_events: '事件',
    reanalyze: '重新分析',
    download: '下载',
    share: '分享',
    ai_verdict: 'AI 判断',
    confidence: '置信度：{{level}}',
    faulting_module: '故障模块',
    stack_frames: '栈帧数',
    modules: '模块',
    uptime_at_crash: '崩溃时运行时长',
    crash_site: '崩溃位置 — 栈顶',
    tab_summary: '摘要',
    tab_report: '报告',
    tab_stack: '调用栈',
    tab_modules: '模块',
    tab_system: '系统',
    tab_files: '文件',
    tab_runs: '运行记录',
    tab_more: '更多',
    meta_description: '描述',
    meta_description_hint: '上传时填写的人工说明，PA 与 SA Leader 可修改。',
    meta_tags_hint: '用于分诊的自由标签，可在事件列表用 tag:value 检索。',
    danger_zone: '删除',
    danger_hint: '从列表中永久移除该事件（软删除，保留审计记录）。',
    delete_event: '删除该事件',
    delete_confirm_title: '删除该事件？',
    delete_confirm_desc: '此操作不可撤销。',
    record: '记录',
    bugcheck: 'Bugcheck',
    suspect: '嫌疑模块',
    machine: '机器',
    source: '来源',
    tracking: '关联',
    code: '代码',
    name: '名称',
    suspect_module: '模块',
    suspect_bucket: '分组',
    suspect_bucket_id: '分组 ID',
    computer: '计算机',
    system: '机型',
    os: '系统',
    cpu: '处理器',
    memory: '内存',
    size: '大小',
    uploaded: '上传于',
    sha: 'SHA-256',
    run: '运行',
    device: '设备',
    case: '案例',
    tags: '标签',
    third_party: '第三方',
    third_party_hint: '非微软签名的模块',
    no_summary: '暂无摘要',
    no_analysis: '正在分析',
    viewing_older_run: '正在查看旧版运行记录（#{{n}}）。最新为运行 #{{latest}}。',
    switch_to_latest: '切换到最新版本 →',
    untitled_analysis: '未命名分析',
    deleted_toast: '事件已删除',
    live_unavailable: '实时更新不可用 — 已回退为每 5 秒轮询。',
    brief: '概要',
    report_pending_hint: 'Worker 正在解析转储，本页会自动更新。',
    report_empty: '暂无分析结果',
    report_empty_hint: '可尝试「重新分析」。',
    files_upload: '上传文件',
    files_stage_hashing: '计算哈希…',
    files_stage_uploading: '上传中…',
    files_stage_finalizing: '确认中…',
    files_upload_progress: '正在上传 {{name}}',
    files_uploaded: '已上传 {{name}}',
    files_upload_failed: '上传失败',
    files_empty: '暂无文件',
    files_col_file: '文件',
    files_col_size: '大小',
    files_col_uploaded: '上传于',
    files_kind_primary: '主转储',
    files_kind_attachment: '附件',
    files_download_aria: '下载 {{name}}',
    runs_run_n: '运行 #{{n}}',
    runs_empty: '暂无运行记录',
    runs_running: '进行中',
    runs_latest: '最新',
    runs_viewing: '正在查看',
    runs_col_run: '运行',
    runs_col_status: '状态',
    runs_col_duration: '耗时',
    runs_col_finished: '完成于',
    verdict_extractor: '提取器',
    share_create_link: '创建链接',
    share_ttl_1d: '1 天',
    share_ttl_7d: '7 天',
    share_copy: '复制',
    share_revoke: '吊销',
    share_revoke_confirm: '吊销该链接？',
    share_expires: '{{date}} 过期',
    share_copied: '链接已复制',
    share_group_link: '分享链接',
    share_ttl_label: '链接有效期',
    share_group_report: '报告',
    share_format_label: '报告格式与语言',
    share_download: '下载',
    share_generate_hint: '按所选格式与语言下载报告',
    share_no_report: '该事件暂无分析报告',
    share_load_failed: '无法加载报告',
    share_export_failed: '报告生成失败',
    share_export_done: '报告已生成',
    share_export_pdf: '正在生成 PDF…',
    share_export_page: '正在渲染第 {{current}} / {{total}} 页…',
    share_export_collecting: '正在收集文档…',
    desc_empty: '暂无描述',
    desc_edit: '编辑',
    desc_add: '添加描述',
    tags_empty: '暂无标签',
    tags_remove: '移除 {{tag}}',
    tags_placeholder: '标签名',
    tags_new: '新建标签',
    no_analysis_yet: '尚未分析',
    no_analysis_hint: 'Worker 解析完成后可用。',
    stack_empty: '暂无栈帧',
    stack_empty_hint: '分析已完成但未采集到栈帧，详见「报告」标签页。',
    stack_count: '{{count}} 个栈帧 · #{{top}} 为崩溃位置',
    modules_empty: '暂无模块',
    modules_empty_hint: '转储的 lm v 输出未包含模块。',
    modules_count: '{{count}} 个模块',
    modules_microsoft: '{{count}} 个微软',
    modules_third_party: '{{count}} 个第三方',
    modules_col_name: '名称',
    modules_col_version: '版本',
    modules_col_company: '厂商',
    modules_col_base: '基址',
    modules_col_size: '大小',
    module_suspect: '可疑',
    sys_identity: '整机信息',
    sys_manufacturer: '制造商',
    sys_product: '产品型号',
    sys_version: '版本',
    sys_family: '系列',
    sys_sku: 'SKU',
    sys_serial: '序列号',
    sys_smbios_uuid: 'SMBIOS UUID',
    sys_last_user: '最后登录用户',
    sys_os: '操作系统',
    sys_os_name: '名称',
    sys_os_version: '版本 / 构建号',
    sys_os_edition: '版本类型',
    sys_os_arch: '架构',
    sys_os_uptime: '崩溃时运行时长',
    sys_cpu: '处理器',
    sys_cpu_vendor: '厂商 / 型号',
    sys_cpu_family: '家族',
    sys_cpu_model: '型号',
    sys_cpu_stepping: '步进',
    sys_cpu_cores: '核心',
    sys_cpu_cores_value: '{{physical}} 物理 · {{logical}} 逻辑',
    sys_cpu_speed: '基准频率',
    sys_cpu_microcode: '微码版本',
    sys_memory: '内存',
    sys_mem_total: '物理内存总量',
    sys_mem_available: '崩溃时可用',
    sys_bios: 'BIOS / 固件',
    sys_bios_vendor: '厂商',
    sys_bios_version: '版本',
    sys_bios_date: '发布日期',
    sys_bios_revision: 'BIOS 修订',
    sys_fw_revision: '固件修订',
    sys_baseboard: '主板',
    sys_adapters: '网络适配器（{{count}}）',
    sys_adapters_empty: '未采集到网卡。',
    sys_adapters_col_name: '名称',
    sys_adapters_col_mac: 'MAC',
    sys_adapters_col_media: '介质',
    sys_adapters_col_driver: '驱动',
    sys_adapters_col_pnp: 'PnP ID',
    sys_adapter_removable: '可插拔',
    sys_adapter_onboard: '板载',
    uptime_na: '—',
  },
};
